import type { Difficulty, GenContext, Question, QuestionKind, QuestionSnapshot } from '@/core/types';
import { catalog } from '@/data/catalog';
import { createRng } from '@/core/util/rng';
import { clamp } from '@/core/util/misc';
import { validateQuestion } from './validate';

/**
 * Skill → Objective → Question Family → Difficulty → Generator → Parameters
 * → Validation → (Evaluation → Feedback → Mastery update happen downstream).
 *
 * Exposure control: recent fingerprints are avoided, and structural "forms"
 * rotate so the student does not see the same task shape twice in a row.
 */

export interface GenerateOptions {
  skillId: string;
  difficulty: number;
  mode: GenContext['mode'];
  recent?: string[];
  preferKinds?: QuestionKind[];
  seed?: number;
}

export const invalidLog: { at: number; skillId: string; issues: string[] }[] = [];

const formOf = (fingerprint: string) => fingerprint.split('|')[1] ?? '';

export function generateQuestion(opts: GenerateOptions): Question | null {
  const skill = catalog.skill(opts.skillId);
  if (!skill) return null;
  const [lo, hi] = skill.difficultyRange;
  const d = clamp(Math.round(opts.difficulty), lo, hi) as Difficulty;
  const rng = createRng(opts.seed);
  const recent = opts.recent ?? [];
  const avoid = new Set<string>(recent);
  const recentForms = recent.slice(-3).map(formOf);
  recentForms.forEach((f) => avoid.add(`form:${skill.id}:${f}`));

  const all = catalog.familiesFor(skill.id);
  if (!all.length) return null;

  let fallback: Question | null = null;
  const tryAt = (dd: Difficulty, attempts: number): Question | null => {
    const inRange = all.filter((f) => dd >= f.difficulties[0] && dd <= f.difficulties[1]);
    const families = inRange.length ? inRange : all;
    let fresh: Question | null = null;
    for (let attempt = 0; attempt < attempts; attempt++) {
      const fam = rng.pick(families);
      const ctx: GenContext = { difficulty: dd, rng, mode: opts.mode, avoid, preferKinds: opts.preferKinds };
      let q: Question | null = null;
      try {
        q = fam.generate(ctx);
      } catch (e) {
        invalidLog.push({ at: Date.now(), skillId: skill.id, issues: [`generator threw: ${(e as Error).message}`] });
        continue;
      }
      if (!q) continue;
      const issues = validateQuestion(q);
      if (issues.length) {
        invalidLog.push({ at: Date.now(), skillId: skill.id, issues });
        continue;
      }
      // fallback = the least recently seen valid candidate
      if (!fallback || recent.lastIndexOf(q.fingerprint) < recent.lastIndexOf(fallback.fingerprint)) fallback = q;
      if (avoid.has(q.fingerprint)) continue;
      if (!fresh) fresh = q;
      // prefer a different structural form than the last few
      if (attempt < attempts * 0.6 && recentForms.includes(formOf(q.fingerprint))) continue;
      return q;
    }
    return fresh;
  };

  const primary = tryAt(d, 16);
  if (primary) return primary;
  // Exposure control: when the pool at this difficulty is exhausted, use an
  // adjacent difficulty rather than repeating a recent question.
  for (const dd of [d + 1, d - 1, d + 2, d - 2]) {
    if (dd < lo || dd > hi) continue;
    const q = tryAt(dd as Difficulty, 8);
    if (q) return q;
  }
  return fallback;
}

export function snapshot(q: Question): QuestionSnapshot {
  return {
    id: q.id, fingerprint: q.fingerprint, skillId: q.skillId, familyId: q.familyId, kind: q.kind, difficulty: q.difficulty,
    prompt: q.prompt, context: q.context, stimulus: q.stimulus, response: q.response, answer: q.answer,
    solution: q.solution, hints: q.hints, speechLang: q.speechLang,
  };
}

/** Worked example: a generated question shown with its full solution. */
export function workedExample(skillId: string, difficulty: number, seed?: number): Question | null {
  return generateQuestion({ skillId, difficulty, mode: 'worked', seed });
}
